/* What the payment platform kept, and what actually reaches the bank.
 *
 * THE ONE PLACE THREE FEE CONVENTIONS BECOME ONE SHAPE. countries.js already
 * owns the three AMOUNT conventions for the same reason; this is the same
 * discipline applied to the other half of the transaction, and for a sharper
 * reason: an amount that is wrong by 100x is caught by a buyer complaining, and
 * a FEE that is wrong by 100x is caught by nobody, because nothing downstream
 * has an expectation of what it should be.
 *
 *   Stripe   balance_transaction   integer minor, in the SETTLEMENT currency
 *   Curlec   payment.fee/.tax      integer minor, in the charge currency (MYR)
 *   HitPay   payments[].fees       DECIMAL MAJOR STRING ("0.24")
 *
 * THE CURRENCY IS THE POINT, not an afterthought. Our Stripe account settles in
 * MYR, so a Hong Kong charge of HKD 7,800 produces a balance transaction
 * denominated in ringgit: its `net` IS the money that lands, conversion cost
 * already taken out. Curlec is ringgit on both sides and needs no thought.
 * HitPay is the odd one out, but not in the way this file first assumed: it
 * prices the CHARGE in SGD and the FEE in MYR (verified against the live API —
 * currency "sgd", amount "1.00", fees "0.63", fees_currency "myr"). So the fee
 * is already the ringgit that left the account, while the gross is not, and
 * HitPay publishes no rate between them. The fee is therefore recorded and the
 * net left null rather than derived from two different currencies; see
 * hitpay() below.
 *
 * NOTHING HERE MULTIPLIES OR DIVIDES BY 100 INLINE. Major/minor conversion goes
 * through countries.js, the same rule the amounts follow.
 */

const { toMinor, toMinorInCurrency, factorForCurrency } = require("./countries");

/* What the bank account is denominated in. Stripe's balance settles here, which
 * is why a Hong Kong charge arrives as a ringgit balance transaction, and
 * HitPay prices its fee here too. One constant rather than three "MYR" literals
 * scattered through the file, so the day a second settlement account exists it
 * is one grep instead of a hunt. */
const SETTLEMENT_CURRENCY = "MYR";

/* The shape every gateway is flattened into, stored as `fee` on the order.
 *
 *   source        which API field this came from, so a wrong number can be
 *                 traced to its origin without guessing
 *   status        available | pending | missing  (see below)
 *   amount        total fee INCLUDING tax, integer minor, in `currency`
 *   tax           the tax portion of it, or null where the gateway doesn't split
 *   net           what lands: gross - fee, integer minor, in `currency`
 *   gross         the charge as the fee currency sees it. NOT order.amount when
 *                 a conversion happened — HKD 7,800 in, MYR 4,000 here.
 *   currency      what amount/net/gross are denominated in
 *   settled       true when `currency` is the money that reaches the bank
 *   exchangeRate  charge currency -> fee currency, when one was applied
 *   breakdown     per-line detail where the gateway gives it
 *   fetchedAt     when we read it, for reconciling against a later restatement
 *
 * STATUS IS NOT DECORATION. A fee can legitimately not exist yet:
 *   - Curlec populates `fee` on capture; an authorized payment carries null.
 *   - Stripe's balance transaction is immediate for cards but pending for
 *     asynchronous methods, and `status: "pending"` means the figures are real
 *     but the money has not landed.
 * Anything reading these numbers must be able to tell "no fee" from "fee not
 * known yet", because summing the second as zero silently overstates the net.
 */

const STATUS = { AVAILABLE: "available", PENDING: "pending", MISSING: "missing" };

/* Not known yet, and saying so. Written in place of a fee rather than leaving
 * the field absent, so a reader can tell an unfetched order from an old one. */
function missing(source, reason) {
  return {
    source: source || null,
    status: STATUS.MISSING,
    reason: reason || null,
    amount: null,
    tax: null,
    net: null,
    gross: null,
    currency: null,
    settled: false,
    exchangeRate: null,
    breakdown: [],
    fetchedAt: Date.now(),
  };
}

const isInt = (v) => Number.isInteger(v);

/* ---------------------------------------------------------------- Stripe ----
 *
 * Everything comes off the BalanceTransaction, which is expanded onto the
 * charge in the one round trip stripe-webhook.js already makes.
 *
 * Its `amount`, `fee` and `net` are in the currency of our Stripe BALANCE, not
 * of the charge — that is the whole reason this is worth reading rather than
 * estimating from a published rate card. For a Malaysian account every foreign
 * charge converts, and `fee_details` carries `stripe_fx_fee` as its own line
 * beside the processing fee, so the cost of selling in HKD is visible
 * separately from the cost of taking a card.
 *
 * `net` is used as given rather than recomputed as amount - fee. They agree by
 * definition, and if Stripe ever disagrees with our arithmetic the right answer
 * is Stripe's: it is the one moving the money.
 */
function stripe(balanceTransaction) {
  const bt = balanceTransaction;
  if (!bt || typeof bt !== "object") {
    return missing("stripe.balance_transaction", "not expanded on the charge");
  }
  if (!isInt(bt.fee) || !isInt(bt.net) || !isInt(bt.amount)) {
    return missing("stripe.balance_transaction", "non-integer amounts");
  }

  const breakdown = Array.isArray(bt.fee_details)
    ? bt.fee_details
        .filter((d) => d && isInt(d.amount))
        .map((d) => ({
          // "stripe_fee", "stripe_fx_fee", "tax" — Stripe's own vocabulary,
          // kept verbatim so a line item can be looked up in their docs.
          type: String(d.type || "unknown"),
          description: d.description || null,
          amount: d.amount,
          currency: String(d.currency || bt.currency || "").toUpperCase(),
        }))
    : [];

  // Stripe reports tax on the fee as a fee_details line rather than a field.
  const taxLine = breakdown.find((d) => d.type === "tax");

  return {
    source: "stripe.balance_transaction",
    // "available" or "pending" straight from Stripe. Pending is not an error:
    // the fee is final, the funds just have not landed in the balance yet.
    status: bt.status === "available" ? STATUS.AVAILABLE : STATUS.PENDING,
    reason: null,
    amount: bt.fee,
    tax: taxLine ? taxLine.amount : null,
    net: bt.net,
    gross: bt.amount,
    currency: String(bt.currency || "").toUpperCase(),
    // The balance currency IS the settlement currency. This is the only one of
    // the three gateways where that is true for a foreign-currency sale.
    settled: true,
    // Null when no conversion happened, i.e. an MYR charge on an MYR balance.
    exchangeRate: typeof bt.exchange_rate === "number" ? bt.exchange_rate : null,
    breakdown,
    fetchedAt: Date.now(),
  };
}

/* ---------------------------------------------------------------- Curlec ----
 *
 * Razorpay puts `fee` and `tax` directly on the payment entity, in the charge
 * currency's subunits. `fee` is INCLUSIVE of `tax` — their docs describe it as
 * "Fee (including GST) charged by Razorpay" — so the two must never be added
 * together. Doing that would overstate the cost by the tax and understate every
 * net by the same amount.
 *
 * Both are null until capture. That is not a failure: the payment.captured
 * webhook is where we read them, and an authorized-only payment has genuinely
 * not been charged a fee yet.
 *
 * Malaysia is the only Curlec market and always will be — every country added
 * since is Stripe — so the charge currency is MYR, it is also the settlement
 * currency, and no conversion is in play.
 */
function curlec(payment) {
  const p = payment;
  if (!p || typeof p !== "object") return missing("curlec.payment", "no payment entity");
  if (!isInt(p.fee)) {
    return missing(
      "curlec.payment",
      p.status === "captured" ? "captured with no fee reported" : `not captured (${p.status || "unknown"})`
    );
  }
  if (!isInt(p.amount)) return missing("curlec.payment", "non-integer amount");

  const tax = isInt(p.tax) ? p.tax : null;
  const currency = String(p.currency || "MYR").toUpperCase();

  return {
    source: "curlec.payment",
    status: STATUS.AVAILABLE,
    reason: null,
    amount: p.fee,
    tax,
    // Razorpay reports no net, so this is the one place it is derived. Safe:
    // both sides are integers in one currency with no conversion between them.
    net: p.amount - p.fee,
    gross: p.amount,
    currency,
    settled: true,
    exchangeRate: null,
    // `fee` already includes `tax`, so the breakdown splits rather than sums.
    breakdown: tax === null
      ? [{ type: "curlec_fee", description: null, amount: p.fee, currency }]
      : [
          { type: "curlec_fee", description: "excluding tax", amount: p.fee - tax, currency },
          { type: "tax", description: null, amount: tax, currency },
        ],
    fetchedAt: Date.now(),
  };
}

/* ---------------------------------------------------------------- HitPay ----
 *
 * `fees` arrives as a decimal major-unit STRING — "0.24" — inside the payments
 * array of a webhook we have already verified. It is the same convention as
 * HitPay's `amount`, and it goes through the same toMinor() the amount does.
 * A fee of "0.24" read as 24 minor units would be right by luck; read as 0.24
 * minor units it is silently zero after rounding.
 *
 * THE HONEST GAP. HitPay reports the fee in the CHARGE currency, so a Singapore
 * PayNow sale gives an SGD fee and an SGD net — but the account settles to a
 * Malaysian bank, and HitPay publishes neither the rate nor the converted
 * figure in this payload. So `settled` is false and `currency` says SGD, which
 * is the truth. It is left to the reader to decide what to do about it rather
 * than converted here at a rate we would have to invent, because an invented
 * rate is indistinguishable from a real one once it is in the database.
 *
 * `country` is passed in rather than inferred: minorFactor is a property of the
 * currency and this file does not get to guess it.
 */
function hitpay(payment, country) {
  const p = payment;
  if (!p || typeof p !== "object") return missing("hitpay.payments[0]", "no payment in the payload");
  if (p.fees === undefined || p.fees === null || p.fees === "") {
    return missing("hitpay.payments[0]", "no fees field in the payload");
  }
  if (!country) return missing("hitpay.payments[0]", "unknown country, cannot size the minor unit");

  const feeMajor = Number(p.fees);
  if (!Number.isFinite(feeMajor) || feeMajor < 0) {
    return missing("hitpay.payments[0]", `unparseable fees value ${JSON.stringify(p.fees)}`);
  }

  const grossMajor = Number(p.amount);
  const haveGross = Number.isFinite(grossMajor) && grossMajor > 0;

  const fee = toMinor(feeMajor, country);
  const gross = haveGross ? toMinor(grossMajor, country) : null;

  /* HitPay's own currency field for the fee, which is NOT the charge currency.
   *
   * Observed against the live API: a Singapore PayNow sale comes back as
   * currency "sgd", amount "1.00", fees "0.63", fees_currency "myr". HitPay
   * prices its fee in the currency the account settles to, so the fee is
   * already the ringgit figure that left the account — better than this file
   * originally assumed, which was that both legs were SGD.
   *
   * It does mean the two legs are in different money and HitPay publishes
   * neither the rate nor a converted gross, so THE NET CANNOT BE DERIVED: the
   * ringgit gross is unknown and subtracting a ringgit fee from a Singapore
   * dollar gross would be nonsense in a way no later reader could detect. The
   * fee is recorded, the net is left null, and `reason` says why. A known cost
   * with an unknown net beats discarding the cost, which is what this did
   * before — four real sales were reported as having no fee at all. */
  const feeCurrency = String(p.fees_currency || p.currency || country.currency || "").toUpperCase();
  const chargeCurrency = String(p.currency || country.currency || "").toUpperCase();
  if (feeCurrency && chargeCurrency && feeCurrency !== chargeCurrency) {
    if (factorForCurrency(feeCurrency) === null) {
      return missing(
        "hitpay.payments[0]",
        `fees are in ${feeCurrency}, which is not a currency we know the subunit of`
      );
    }
    return {
      source: "hitpay.payments[0]",
      status: STATUS.AVAILABLE,
      reason:
        `fee is in ${feeCurrency}, the charge in ${chargeCurrency}; ` +
        "HitPay publishes no rate, so the net cannot be derived",
      // Sized by the FEE's currency, not the buyer's. Right by luck while both
      // are 100; wrong the day either side is zero-decimal.
      amount: toMinorInCurrency(feeMajor, feeCurrency),
      tax: null,
      // Both null on purpose. See above: there is no ringgit gross to subtract
      // from, and a null is the only honest answer a reader cannot misread.
      net: null,
      gross: null,
      currency: feeCurrency,
      // The fee IS denominated in what reaches the bank. That is the whole
      // reason this branch records a figure instead of giving up.
      settled: feeCurrency === SETTLEMENT_CURRENCY,
      exchangeRate: null,
      breakdown: [
        { type: "hitpay_fee", description: null, amount: toMinorInCurrency(feeMajor, feeCurrency), currency: feeCurrency },
      ],
      fetchedAt: Date.now(),
    };
  }

  return {
    source: "hitpay.payments[0]",
    status: STATUS.AVAILABLE,
    reason: null,
    amount: fee,
    // HitPay does not split tax out of the fee in this payload.
    tax: null,
    net: gross === null ? null : gross - fee,
    gross,
    currency: chargeCurrency,
    /* FALSE ON PURPOSE whenever the charge is not already in ringgit: this
     * branch runs when HitPay priced both legs in the same currency, so a
     * Singapore sale gives an SGD fee and an SGD net, and the money reaching
     * the bank is neither. Any total that mixes it with the other two without
     * saying so is wrong. */
    settled: chargeCurrency === SETTLEMENT_CURRENCY,
    exchangeRate: null,
    breakdown: [
      { type: "hitpay_fee", description: null, amount: fee, currency: chargeCurrency },
    ],
    fetchedAt: Date.now(),
  };
}

/* ------------------------------------------------------------- reporting ---
 *
 * What a row contributes to "money in the pocket". Kept here, beside the
 * normalisation, so the endpoint and the backfill cannot disagree about it.
 *
 * REFUNDS COST MORE THAN THE SALE RETURNED. Stripe's pricing page is explicit
 * that "the payment processing, Connect and currency conversion fees from the
 * original transaction are not returned", and Curlec is the same. So a fully
 * refunded order nets NEGATIVE the fee — it is not a wash, and treating it as
 * one overstates the pocket by roughly 6% of that sale, permanently. This is
 * the single easiest number in the whole report to get wrong in the flattering
 * direction, which is why it is computed in one place and asserted in tests.
 */
function pocket(order) {
  const fee = order && order.fee;
  if (!fee || fee.status === STATUS.MISSING || !isInt(fee.net)) return null;

  const refunded = isInt(order.refundedAmount) ? order.refundedAmount : 0;
  if (refunded <= 0) return { amount: fee.net, currency: fee.currency, settled: fee.settled };

  /* The refund returns gross, never the fee. Refunding everything therefore
   * leaves us down by exactly the fee that was charged to take the money. */
  const grossBack = fee.gross === null ? refunded : Math.min(refunded, fee.gross);
  return {
    amount: fee.net - grossBack,
    currency: fee.currency,
    settled: fee.settled,
    refunded: grossBack,
  };
}

/* Fee as a share of what the buyer paid, for the one figure that is comparable
 * across markets. Returned as a fraction; the caller decides the formatting. */
function rate(fee) {
  if (!fee || !isInt(fee.amount) || !isInt(fee.gross) || fee.gross <= 0) return null;
  return fee.amount / fee.gross;
}

module.exports = { STATUS, missing, stripe, curlec, hitpay, pocket, rate };
